import { useCallback, useRef } from 'react';
import type { Candle, TimeFrame } from '../types/trading';

/**
 * Calculates the time bucket (candle start time) for a given timestamp and timeframe.
 * Returns time in SECONDS (for lightweight-charts compatibility).
 */
export const getCandleTimeBucket = (timestampMs: number, timeframe: TimeFrame): number => {
  const date = new Date(timestampMs);

  switch (timeframe) {
    case '1m':
      return Math.floor(timestampMs / 60000) * 60; // 1 minute in seconds
    case '5m':
      return Math.floor(timestampMs / 300000) * 300; // 5 minutes
    case '15m':
      return Math.floor(timestampMs / 900000) * 900; // 15 minutes
    case '1h':
      return Math.floor(timestampMs / 3600000) * 3600; // 1 hour
    case '4h':
      return Math.floor(timestampMs / 14400000) * 14400; // 4 hours
    case '1D':
      // Start of day in UTC
      date.setUTCHours(0, 0, 0, 0);
      return Math.floor(date.getTime() / 1000);
    default:
      return Math.floor(timestampMs / 3600000) * 3600; // Default to 1h
  }
};

/**
 * Gets the duration of a timeframe in milliseconds.
 */
export const getTimeframeDurationMs = (timeframe: TimeFrame): number => {
  switch (timeframe) {
    case '1m': return 60 * 1000;
    case '5m': return 5 * 60 * 1000;
    case '15m': return 15 * 60 * 1000;
    case '1h': return 60 * 60 * 1000;
    case '4h': return 4 * 60 * 60 * 1000;
    case '1D': return 24 * 60 * 60 * 1000;
    default: return 60 * 60 * 1000;
  }
};

interface UseCandleUpdaterOptions {
  timeframe: TimeFrame;
  onCandleUpdate?: (symbol: string, candle: Candle, isNew: boolean) => void;
}

interface CandleUpdaterResult {
  /** Update candle with a new price tick */
  updateWithPrice: (symbol: string, price: number, timestampMs?: number) => Candle | null;
  /** Set initial candles for a symbol */
  setCandles: (symbol: string, candles: Candle[]) => void;
  /** Get current candles for a symbol */
  getCandles: (symbol: string) => Candle[];
  /** Clear candles for a symbol */
  clearCandles: (symbol: string) => void;
}

/**
 * Hook for managing real-time candle updates with proper time bucket detection.
 *
 * This implements the TradingView/Bitsgap pattern:
 * - Historical candles loaded via REST API
 * - Real-time ticks update the "live" candle
 * - When timeframe boundary is crossed, a new candle is created
 */
export const useCandleUpdater = ({
  timeframe,
  onCandleUpdate,
}: UseCandleUpdaterOptions): CandleUpdaterResult => {
  // Store candles in refs to avoid re-renders on every tick
  const candlesRef = useRef<Record<string, Candle[]>>({});
  // Track last update time for each symbol to detect gaps
  const lastUpdateRef = useRef<Record<string, number>>({});

  const setCandles = useCallback((symbol: string, candles: Candle[]) => {
    candlesRef.current[symbol] = [...candles];
    if (candles.length > 0) {
      lastUpdateRef.current[symbol] = candles[candles.length - 1].time * 1000;
    }
  }, []);

  const getCandles = useCallback((symbol: string): Candle[] => {
    return candlesRef.current[symbol] || [];
  }, []);

  const clearCandles = useCallback((symbol: string) => {
    delete candlesRef.current[symbol];
    delete lastUpdateRef.current[symbol];
  }, []);

  const updateWithPrice = useCallback((
    symbol: string,
    price: number,
    timestampMs: number = Date.now()
  ): Candle | null => {
    const candles = candlesRef.current[symbol];
    if (!candles || candles.length === 0) {
      return null;
    }

    // Calculate which time bucket this price belongs to
    const bucket = getCandleTimeBucket(timestampMs, timeframe);
    const lastCandle = candles[candles.length - 1];

    if (lastCandle.time === bucket) {
      // Same candle - UPDATE existing
      const updatedCandle: Candle = {
        ...lastCandle,
        high: Math.max(lastCandle.high, price),
        low: Math.min(lastCandle.low, price),
        close: price,
      };
      candles[candles.length - 1] = updatedCandle;
      lastUpdateRef.current[symbol] = timestampMs;

      onCandleUpdate?.(symbol, updatedCandle, false);
      return updatedCandle;
    } else if (bucket > lastCandle.time) {
      // New candle - CREATE
      const newCandle: Candle = {
        time: bucket,
        open: price,
        high: price,
        low: price,
        close: price,
        volume: 0,
      };
      candles.push(newCandle);
      lastUpdateRef.current[symbol] = timestampMs;

      // Keep max 2000 candles to prevent memory issues
      if (candles.length > 2000) {
        candles.shift();
      }

      onCandleUpdate?.(symbol, newCandle, true);
      return newCandle;
    }

    // Price is for an old candle (shouldn't happen normally)
    return null;
  }, [timeframe, onCandleUpdate]);

  return {
    updateWithPrice,
    setCandles,
    getCandles,
    clearCandles,
  };
};

export default useCandleUpdater;
